import { logger } from './logger';

export interface NormalizedPracticeData {
  task: string;
  starter: string;
  solution?: string;
  hints: string[];
  estimatedTime?: number;
  difficulty?: string;
  type?: string;
}

/**
 * Normalizes practice lab data from various JSONB structures in the database
 * to a consistent format for UI consumption
 */
export class PracticeDataNormalizer {
  /**
   * Normalize practice data from any supported structure
   */
  static normalize(rawData: any, courseType?: string): NormalizedPracticeData | null {
    if (!rawData) {
      logger.warn('PracticeDataNormalizer: No data provided');
      return null;
    }

    // If already in correct format, return as-is
    if (this.isValidNormalizedFormat(rawData)) {
      return rawData as NormalizedPracticeData;
    }

    // Try different normalization strategies
    const normalized = 
      this.normalizeFromExercises(rawData) ||
      this.normalizeFromScenarios(rawData) ||
      this.normalizeFromTasks(rawData) ||
      this.normalizeFromLegacyFormat(rawData) ||
      this.normalizeFromDirectFields(rawData);

    if (normalized) {
      logger.debug('PracticeDataNormalizer: Successfully normalized data', {
        sourceType: this.detectSourceType(rawData),
        courseType
      });
      return normalized;
    }

    logger.error('PracticeDataNormalizer: Failed to normalize data', { rawData });
    return null;
  }

  /**
   * Check if data is already in normalized format
   */
  private static isValidNormalizedFormat(data: any): boolean {
    return (
      typeof data === 'object' &&
      typeof data.task === 'string' &&
      typeof data.starter === 'string' &&
      Array.isArray(data.hints)
    );
  }

  /**
   * Normalize from exercises array format
   */
  private static normalizeFromExercises(data: any): NormalizedPracticeData | null {
    if (!Array.isArray(data.exercises) || data.exercises.length === 0) {
      return null;
    }

    const firstExercise = data.exercises[0];
    
    return {
      task: firstExercise.task || firstExercise.description || firstExercise.title || 'Complete the practice exercise',
      starter: firstExercise.starter || firstExercise.starterCode || firstExercise.starter_code || '',
      solution: firstExercise.solution || firstExercise.solutionCode || firstExercise.solution_code,
      hints: firstExercise.hints || [],
      estimatedTime: data.estimated_time || data.estimatedTime,
      difficulty: data.difficulty_level || data.difficulty,
      type: 'exercises'
    };
  }

  /**
   * Normalize from scenarios array format
   */
  private static normalizeFromScenarios(data: any): NormalizedPracticeData | null {
    if (!Array.isArray(data.scenarios) || data.scenarios.length === 0) {
      return null;
    }

    const firstScenario = data.scenarios[0];
    
    return {
      task: firstScenario.scenario || firstScenario.description || 'Complete the scenario',
      starter: firstScenario.starter_code || firstScenario.starter || '',
      solution: firstScenario.solution,
      hints: firstScenario.hints || [],
      estimatedTime: data.estimated_time || data.estimatedTime,
      difficulty: data.difficulty_level || data.difficulty,
      type: 'scenarios'
    };
  }

  /**
   * Normalize from tasks array format
   */
  private static normalizeFromTasks(data: any): NormalizedPracticeData | null {
    if (!Array.isArray(data.tasks) || data.tasks.length === 0) {
      return null;
    }

    const firstTask = data.tasks[0];
    
    return {
      task: firstTask.description || firstTask.task || 'Complete the task',
      starter: firstTask.starter_code || firstTask.starter || '',
      solution: firstTask.solution,
      hints: firstTask.hints || [],
      estimatedTime: data.estimated_time || data.estimatedTime,
      difficulty: data.difficulty_level || data.difficulty,
      type: 'tasks'
    };
  }

  /**
   * Normalize from legacy format (direct fields at root level)
   */
  private static normalizeFromLegacyFormat(data: any): NormalizedPracticeData | null {
    // Check if data has direct fields (no nested arrays)
    if (data.task || data.description || data.starter || data.starter_code) {
      return {
        task: data.task || data.description || 'Complete the practice lab',
        starter: data.starter || data.starter_code || data.starterCode || '',
        solution: data.solution || data.solution_code || data.solutionCode,
        hints: data.hints || [],
        estimatedTime: data.estimated_time || data.estimatedTime,
        difficulty: data.difficulty_level || data.difficulty,
        type: 'legacy'
      };
    }

    return null;
  }

  /**
   * Normalize from direct fields (flat structure)
   */
  private static normalizeFromDirectFields(data: any): NormalizedPracticeData | null {
    if (typeof data !== 'object' || data === null) {
      return null;
    }

    // Last resort: try to extract any usable fields
    const task = data.task || data.description || data.instructions || '';
    const starter = data.starter || data.starter_code || data.starterCode || data.code || '';
    
    if (!task && !starter) {
      return null;
    }

    return {
      task: task || 'Complete the practice exercise',
      starter: starter,
      solution: data.solution || data.solution_code || data.solutionCode,
      hints: Array.isArray(data.hints) ? data.hints : [],
      estimatedTime: data.estimated_time || data.estimatedTime,
      difficulty: data.difficulty_level || data.difficulty,
      type: 'direct'
    };
  }

  /**
   * Detect the source type of the data structure
   */
  private static detectSourceType(data: any): string {
    if (Array.isArray(data.exercises)) return 'exercises';
    if (Array.isArray(data.scenarios)) return 'scenarios';
    if (Array.isArray(data.tasks)) return 'tasks';
    if (data.task || data.starter) return 'direct';
    return 'unknown';
  }

  /**
   * Validate that normalized data has minimum required fields
   */
  static validate(data: NormalizedPracticeData | null): boolean {
    if (!data) return false;
    
    const hasTask = typeof data.task === 'string' && data.task.length > 0;
    const hasStarter = typeof data.starter === 'string';
    const hasHints = Array.isArray(data.hints);
    
    return hasTask && hasStarter && hasHints;
  }
}
